import type {FilterChip} from '@/modules/common/hooks/useFilterChips';

import styled from '@emotion/styled';
import {ActionLink, Chip, ChipGroup} from '@travel-frontend/ui-components';
import {noop} from 'lodash';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {INVIA_TYPE_DISCOUNT} from '@/consts';
import {useTourOperatorDiscountCampaign} from '@/modules/common/hooks';
import {useCurrentPage} from '@/modules/common/hooks/useCurrentPage';
import {EVENT_FILTER_REMOVE} from '@/redux/middleware/googleTagManager';
import {testId} from '@/utils/testId';

type Props = {
  filterChips: Array<FilterChip>;
  onClickChip?: () => void;
  onClickReset?: () => void;
};

export const FilterChips = styled(
  ({
    filterChips,
    onClickChip = noop,
    onClickReset = noop,
    ...restProps
  }: Props) => {
    const dispatch = useDispatch();
    const {t} = useTranslation('filters');
    const currentPage = useCurrentPage();

    const createClickHandler =
      (id: string, clear: () => void) => (event: React.MouseEvent) => {
        // otherwise `onClickRemove` would also trigger `onClick` due to event bubbling
        event.stopPropagation();
        const nextValue = clear();
        dispatch({
          type: EVENT_FILTER_REMOVE,
          payload: {filters: [[id, nextValue]], currentPage},
        });
        onClickChip();
      };

    // IT08P-DISC#DISC
    const {shouldShowFilter, filterInterceptorContent} =
      useTourOperatorDiscountCampaign();

    return (
      <ChipGroup {...restProps} data-testid={testId('chosenFilter')}>
        {filterChips.map(({id, label, clear, type}) => {
          const chipLabel =
            shouldShowFilter && id === INVIA_TYPE_DISCOUNT
              ? `${filterInterceptorContent.text} ${filterInterceptorContent.highlight}`
              : label;

          return (
            <Chip
              active
              key={id}
              variant="outlined"
              size="small"
              label={chipLabel}
              onClick={createClickHandler(type ?? id, clear)}
              onClickRemove={createClickHandler(type ?? id, clear)}
            />
          );
        })}
        <ActionLink size="medium" bold onClick={onClickReset}>
          {t('chips.clearActiveFilters')}
        </ActionLink>
      </ChipGroup>
    );
  }
)`
  ${Chip} {
    max-width: 280px;
  }

  /* @see [https://invia.atlassian.net/browse/FFP-3326] Filter reset link is aligned incorrectly in Firefox */
  ${ActionLink} {
    align-self: center;
  }
`;
